// Parses OrderMate's "Sales and Takings Analysis" CSV export (Reports > Sales-Takings),
// which has one row per trading day for any date range.

export interface ParsedSalesDay {
  trade_date: string;            // YYYY-MM-DD
  total_sales: number | null;
  food_sales: number | null;
  bev_sales: number | null;
  other_sales: number | null;
  discounts: number | null;
  surcharges: number | null;
  net_ex_gst: number | null;
  tax: number | null;
  covers: number | null;
  payments: Record<string, number>;
}

// Minimal CSV line parser: handles quoted fields with commas and "" escapes.
function parseLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let q = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (q) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') q = false;
      else cur += ch;
    } else if (ch === '"') q = true;
    else if (ch === ',') { out.push(cur); cur = ''; }
    else cur += ch;
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

const money = (s: string | undefined): number | null => {
  if (s == null || s === '') return null;
  const neg = /^-|\(/.test(s);
  const n = Number(s.replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? (neg ? -n : n) : null;
};

// Payment columns we keep (header -> key). Anything else that looks like money is kept under its own name.
const PAYMENT_KEYS: Record<string, string> = {
  'Cash': 'cash', 'Visa': 'visa', 'Mastercard': 'mastercard', 'Amex': 'amex', 'Diners': 'diners', 'JCB': 'jcb',
  'Alipay': 'alipay', 'Bankcard': 'bankcard', 'Paper Cheque': 'paper_cheque', 'Cheque Acc': 'cheque_acc',
  'Savings Acc': 'savings_acc', 'Internet Payment': 'internet_payment', 'SevenRooms': 'sevenrooms',
  'Petty Cash': 'petty_cash', 'Voucher Redeem': 'voucher_redeem', 'Voucher Topup': 'voucher_topup', 'Debtors': 'debtors',
};
const SALES_COLS = new Set(['Date', '(Not allocated)', 'Beverage', 'Food', 'Messages & Allergies', 'Total', 'Dis', 'Sur', 'Net*', 'Tax Free', 'Total Tax', 'Gross', 'System Takings', 'Actual Takings', 'Variance', 'Covers']);

export function parseSalesAndTakings(text: string): { days: ParsedSalesDay[]; error?: string } {
  const lines = text.replace(/^﻿/, '').split(/\r?\n/).filter((l) => l.trim());
  const headerIdx = lines.findIndex((l) => /"?Date"?\s*,/.test(l) && /Food/.test(l) && /Total/.test(l));
  if (headerIdx < 0) return { days: [], error: 'This doesn\'t look like OrderMate\'s "Sales and Takings Analysis" export. Please export that report as CSV.' };
  const header = parseLine(lines[headerIdx]);
  const col = (name: string) => header.indexOf(name);
  const days: ParsedSalesDay[] = [];
  for (const line of lines.slice(headerIdx + 1)) {
    const cells = parseLine(line);
    const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(cells[col('Date')] ?? '');
    if (!m) continue; // skips "Total" and "Gross %" rows
    const get = (name: string) => (col(name) >= 0 ? money(cells[col(name)]) : null);
    const payments: Record<string, number> = {};
    header.forEach((h, i) => {
      if (SALES_COLS.has(h)) return;
      const v = money(cells[i]);
      if (v != null && v !== 0) payments[PAYMENT_KEYS[h] ?? h.toLowerCase().replace(/[^a-z0-9]+/g, '_')] = v;
    });
    const other = [get('(Not allocated)'), get('Messages & Allergies')].reduce<number>((a, b) => a + (b ?? 0), 0);
    const coversRaw = col('Covers') >= 0 ? Number(String(cells[col('Covers')]).replace(/[^0-9]/g, '')) : NaN;
    // Closed days (Sundays, public holidays) export as all-zero rows: skip them so averages stay true.
    if (!(get('Total') ?? 0) && !(get('Gross') ?? 0) && !(Number.isFinite(coversRaw) && coversRaw > 0)) continue;
    days.push({
      trade_date: `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`,
      total_sales: get('Total') ?? get('Gross'),
      food_sales: get('Food'),
      bev_sales: get('Beverage'),
      other_sales: other || 0,
      discounts: get('Dis'),
      surcharges: get('Sur'),
      net_ex_gst: get('Net*'),
      tax: get('Total Tax'),
      covers: Number.isFinite(coversRaw) ? coversRaw : null,
      payments,
    });
  }
  if (!days.length) return { days, error: 'No daily rows found in this file.' };
  return { days };
}
